import React, { useContext, useState, useEffect } from 'react';
import NewsletterSignup from './NewsletterSignup';
import yellowBird from "../assets/yellow-bird.png";
import './style.css';
import { PushComponent } from './Push';
import darkModeLogo from '../assets/darkmode-logo.webp';
import ThemeContext from "../../contexts/ThemeContext"; 
import Logo from '../assets/logo-jpg.webp';
import USPAndGPPFooter from "./consentManagement/USPAndGPPFooter";

const MenuItem = ({ href, label }) => (
  <li className="list-item">
    <a href={href} className="compatible-link cursor-pointer inline-block text-[0.88rem] font-medium capitalize">
      {label}
    </a>
  </li>
);

const Section = ({ title, links, components }) => (
  <div className="flex-grow">
    <h3 className="text-[0.81rem] font-bold uppercase">{title}</h3>
    <ul>
      {links.map((link, index) => (
        <MenuItem key={index} {...link} />
      ))}
      {components}
    </ul>
  </div>
);

const Accordion = ({ title, links = [], components = [] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4">
      <h3 
        onClick={() => setIsOpen(!isOpen)} 
        className="text-[0.81rem] font-bold uppercase cursor-pointer border-b"
      >
        {title}
      </h3>
      {components.map((component, index) => (
        <div key={`component-${index}`}>{component}</div>
      ))}
      <ul className={`overflow-hidden transition-max-height duration-700 ${isOpen ? 'max-h-screen' : 'max-h-0'}`}>
        {links.map((link, index) => (
          <MenuItem key={`link-${index}`} {...link} />
        ))}
      </ul>
    </div>
  );
};

const USP_CONSENT_KEY = "uspConsentState";
const GPP_CONSENT_KEY = "gppConsentState";

const Footer = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const [consent, setConsent] = useState({
    uspConsent: true,  // Default to consent given
    uspString: "1YNN",
    gppConsent: true,
    gppString: "GPP0",
  });

  useEffect(() => {
    const getStoredConsent = (key, defaultValue) => {
      try {
        const storedValue = localStorage.getItem(key);
        return storedValue ? JSON.parse(storedValue) : defaultValue;
      } catch (error) {
        console.error(`Error parsing ${key} from localStorage:`, error);
        return defaultValue;
      }
    };

    // Retrieve stored consent or default to "Opted-In"
    const storedUSP = getStoredConsent(USP_CONSENT_KEY, { uspConsent: true, uspString: "1YNN" });
    const storedGPP = getStoredConsent(GPP_CONSENT_KEY, { gppConsent: true, gppString: "GPP0" });

    setConsent({
      uspConsent: storedUSP.uspConsent,
      uspString: storedUSP.uspString,
      gppConsent: storedGPP.gppConsent,
      gppString: storedGPP.gppString,
    });

    // Ensure `localStorage` has correct values only if user hasn't opted out
    if (storedUSP.uspConsent) {
      localStorage.setItem(USP_CONSENT_KEY, JSON.stringify(storedUSP));
    }
    if (storedGPP.gppConsent) {
      localStorage.setItem(GPP_CONSENT_KEY, JSON.stringify(storedGPP));
    }
  }, []); 

  // Function to update consent and store/remove it from `localStorage`
  const updateUSPAndGPP = (newConsent) => {
    setConsent(newConsent);

    if (newConsent.uspConsent) {
      localStorage.setItem(USP_CONSENT_KEY, JSON.stringify({
        uspConsent: newConsent.uspConsent,
        uspString: newConsent.uspString
      }));
    } else {
      localStorage.removeItem(USP_CONSENT_KEY); //Remove USP consent if opted out
    }

    if (newConsent.gppConsent) {
      localStorage.setItem(GPP_CONSENT_KEY, JSON.stringify({
        gppConsent: newConsent.gppConsent,
        gppString: newConsent.gppString
      }));
    } else {
      localStorage.removeItem(GPP_CONSENT_KEY); //Remove GPP consent if opted out
    }
  };

  const sections = [
    { title: 'News', links: [
      { href: "https://www.trustnodes.com/", label: "Home Page" },
      { href: "https://www.trustnodes.com/news/usa", label: "USA" },
      { href: "https://www.trustnodes.com/news/europe", label: "Europe" },
      { href: "https://www.trustnodes.com/news/asia", label: "Asia" },
      { href: "https://www.trustnodes.com/news/opinion", label: "Opinion" },
      { href: "https://www.trustnodes.com/news/politics", label: "Politics" },
      { href: "https://www.trustnodes.com/news/cryptopolitics", label: "Crypto-Politics" },
      { href: "https://www.trustnodes.com/news/blockchain", label: "Blockchain" },
      { href: "https://www.trustnodes.com/news/tech", label: "Tech" },
      // ... add more links as needed
    ]},
    { title: 'Crypto', links: [
      { href: "https://www.trustnodes.com/news/ethereum", label: "Ethereum" },
      { href: "https://www.trustnodes.com/news/bitcoin", label: "Bitcoin" },
      { href: "https://www.trustnodes.com/news/l2", label: "Second Layers (L2)" },
      { href: "https://www.trustnodes.com/news/staking", label: "Staking" },
      { href: "https://www.trustnodes.com/news/tokens", label: "Tokens" },
      { href: "https://www.trustnodes.com/news/mining", label: "Mining" },
      { href: "https://www.trustnodes.com/news/crypto-commerce", label: "Crypto-Commerce" },
      { href: "https://www.trustnodes.com/news/stablecoins", label: "Stablecoins" },
      // ... add more links as needed
    ]},
    { title: 'Markets', links: [
      { href: "https://www.trustnodes.com/news/finance", label: "Finance" },
      { href: "https://www.trustnodes.com/news/stocks", label: "Stocks" },
      { href: "https://www.trustnodes.com/news/trading", label: "Trading" },
      { href: "https://www.trustnodes.com/news/fintech", label: "FinTech" },
      { href: "https://www.trustnodes.com/news/gold", label: "Gold" },
      { href: "https://www.trustnodes.com/news/commodities", label: "Commodities" },
      // ... add more links as needed
    ]},
    { title: 'Web3', links: [
      { href: "https://www.trustnodes.com/news/defi", label: "DeFi" },
      { href: "https://www.trustnodes.com/news/metaverse", label: "Metaverse" },
      { href: "https://www.trustnodes.com/news/nfts", label: "NFT" },
      { href: "https://www.trustnodes.com/news/daos", label: "DAOs" },
      { href: "https://www.trustnodes.com/news/dapps", label: "Dapps" },

      // ... add more links as needed
    ]},
    { title: 'Culture', links: [
      { href: "https://www.trustnodes.com/news/art", label: "Art" },
      { href: "https://www.trustnodes.com/news/gaming", label: "Gaming" },
      { href: "https://www.trustnodes.com/news/memes", label: "Memes" },
      { href: "https://www.trustnodes.com/news/music", label: "Music" },

      // ... add more links as needed
    ]},

      {
          title: 'Newsletter',
          components: [
            <NewsletterSignup key="newsletter" />,

             <div className="pt-4" key="privacy-section">

                <USPAndGPPFooter consent={consent} updateUSPAndGPP={updateUSPAndGPP} />

              </div>
          ],
          links: []
        },


        {
          title: '',
          components: [

            <PushComponent key="push" />,

          ],
          links: []
        },

    // ... add more sections as needed
  ];

  return (
    <>
    <div className="text-zinc-800 pb-2 px-9">
      <div>
        <div className="border-t border-b-2 border-gray-200 mb-1 mt-8"></div>
        <div className="flex justify-between ">
            <div className="flex justify-between text-zinc-800 flex w-full ">
                <a href="https://www.trustnodes.com/" className="cursor-pointer">
                  <img 
                    className={`align-middle truncate ${isDarkMode ? "h-6 w-34" : "h-8 w-34 -ml-5"}`} 
                    src={isDarkMode ? darkModeLogo : Logo} 
                    alt="Logo" 
                  />
                </a>

                <div className="mt-2"><a href="https://www.trustnodes.com/" className="text-slate-500 cursor-pointer text-[0.69rem] custom-link">Go to Home Page »</a><
                /div>
            </div>
          
        </div>
        <div className="hidden-sm-block compatible-link">
          <div className="hidden-sm-block flex mt-3">
              {sections.map((section, index) => (
                <Section 
                  key={index} 
                  title={section.title}   
                  links={section.links} 
                  components={section.components} 
                />
              ))}
            </div>
        </div>
        <div className="md:hidden compatible-link">
            <div style={{color: 'var(--text-color)'}} className="md:hidden flex flex-col md:flex-row mt-3">
              {sections.map((section, index) => (
                <Accordion 
                  key={index} 
                  title={section.title} 
                  links={section.links}
                  components={section.components}
                />
              ))}
            </div>
        </div>
      </div>
    </div>
    <div className="text-zinc-800 text-[0.69rem] pb-2 px-9 text-center custom-link">
      <div className="pt-2 border-gray-200 border-t">
        <ul className="inline-block mb-3.5">
          <li className="list-item">
            <div className="-ml-1 text-stone-500 cursor-pointer py-3 custom-link">
              ©&nbsp;2017-2025&nbsp;Trustnodes 
            </div>
          </li>
        </ul>
        <ul className="inline-block mb-9">
          <li className="inline-block px-3">
            <a href="https://www.trustnodes.com/privacy-policy" className="text-stone-500 cursor-pointer py-3 custom-link">Privacy Policy</a>
          </li>
          <li className="inline-block px-3">
            <a href="https://www.trustnodes.com/terms-of-service" className="text-stone-500 cursor-pointer py-3 custom-link">Terms of Service</a>
          </li>
          <li className="inline-block px-3">
            <a href="https://www.trustnodes.com/our-values-and-ethics" className="text-stone-500 cursor-pointer py-3 custom-link">Our Ethics and Values</a>
          </li>
          <li className="inline-block px-3 custom-link">
            For any enquiries contact@trustnodes.com 
          </li>

          <li className="inline-block px-3">
            <a href="https://www.trustnodes.com/feed" className="text-stone-500 cursor-pointer py-3 custom-link">RSS Feed</a>
          </li>
          {/* Include other list items here... */}
        </ul>
      </div>
    </div>
    <div className="flex justify-between gap-4 mb-8 px-9">
        <img className="w-8 overflow-clip" src={yellowBird}/>
        <img className="w-8 overflow-clip" src={yellowBird}/>
        <img className="w-8 overflow-clip" src={yellowBird}/>
    </div>
    </>
  );
};

export default Footer;



